/**
 * What was going on when an error was produced, for the issue tracker.
 *
 * Kept beside the error objects, not on them (the way userFacing.ts marks
 * errors as shown): the GraphQL link notes the operation of every error it
 * sees pass (trackingLink.ts), and the phantom-testing flow notes the study
 * and the routine. issueTracker.ts reads both when the error is reported.
 */

export interface OperationContext {
  /** the operation's name, e.g. testStudy */
  name: string
  type: string
  /** the operation's variables, secrets already redacted */
  variables?: unknown
}

export interface TestContext {
  studyId: number
  routineId?: number
}

export interface ErrorContext {
  operation?: OperationContext
  test?: TestContext
}

const contexts = new WeakMap<object, ErrorContext>()

const isObject = (value: unknown): value is { [key: string]: unknown } =>
  typeof value === 'object' && value !== null

/** Note `context` for `error`; later notes add to earlier ones. Ignores non-objects. */
export function addErrorContext(error: unknown, context: ErrorContext): void {
  if (!isObject(error)) {
    return
  }
  contexts.set(error, { ...contexts.get(error), ...context })
}

/**
 * The error objects a thrown value is made of: itself, then what it wraps or
 * lists (a zone.js `rejection`, a HandledFailure's `cause`, an ApolloError's
 * `graphQLErrors` and `networkError`, the items of a `result.errors` list).
 */
function parts(thrown: unknown, depth = 0): object[] {
  if (!isObject(thrown) || depth > 3) {
    return []
  }
  const found: object[] = [thrown]
  const inner: unknown[] = Array.isArray(thrown)
    ? thrown
    : [
        thrown.rejection,
        thrown.cause,
        ...(Array.isArray(thrown.graphQLErrors) ? thrown.graphQLErrors : []),
        thrown.networkError,
      ]
  inner.forEach((part) => found.push(...parts(part, depth + 1)))
  return found
}

/**
 * Everything noted for a thrown value or for the errors inside it. Where two
 * notes disagree, the one closest to the thrown value wins.
 */
export function getErrorContext(thrown: unknown): ErrorContext {
  return parts(thrown)
    .reverse()
    .reduce<ErrorContext>(
      (merged, part) => ({ ...merged, ...contexts.get(part) }),
      {},
    )
}
